import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { UploadFile as UploadFileIcon, FactCheck as PreviewIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService, TeamMemberImportResult } from '../../services/api.service';
import { ContestFormDialogLayout } from './ContestFormDialogLayout';

interface TeamMemberImportFormProps {
  contestId: string;
  onImported: (result: TeamMemberImportResult) => void;
  onCancel: () => void;
}

// Two steps: paste/upload `email,team` rows and preview (dry run), then confirm to write.
// Editing the text discards the preview so what gets imported is always what was previewed.
export const TeamMemberImportForm = ({ contestId, onImported, onCancel }: TeamMemberImportFormProps) => {
  const { t } = useTranslation('contests');
  const fileInput = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<TeamMemberImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCsv = (text: string) => {
    setCsv(text);
    setPreview(null);
    setError(null);
  };

  const loadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) updateCsv(await file.text());
  };

  const run = async (dryRun: boolean) => {
    try {
      setBusy(true);
      setError(null);
      const result = await apiService.importTeamMembers(contestId, csv, dryRun);
      if (dryRun) setPreview(result);
      else onImported(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('importMembers.error'));
    } finally {
      setBusy(false);
    }
  };

  const totalAdding = preview?.teams.reduce((sum, team) => sum + team.adding, 0) ?? 0;

  const footer = (
    <>
      <Button variant="outlined" onClick={onCancel} disabled={busy}>{t('common:actions.cancel')}</Button>
      {preview ? (
        <Button variant="contained" onClick={() => run(false)} disabled={busy || totalAdding === 0} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {t('importMembers.confirm', { count: totalAdding })}
        </Button>
      ) : (
        <Button variant="contained" onClick={() => run(true)} disabled={busy || !csv.trim()} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PreviewIcon />}>
          {t('importMembers.preview')}
        </Button>
      )}
    </>
  );

  return (
    <ContestFormDialogLayout title={t('importMembers.title')} subtitle={t('importMembers.subtitle')} onClose={onCancel} footer={footer}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ fontSize: 13, color: 'var(--muted)' }}>{t('importMembers.formatHint')}</Box>
        <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInput.current?.click()} disabled={busy}>
          {t('importMembers.uploadCsv')}
        </Button>
        <input ref={fileInput} type="file" accept=".csv,text/csv,text/plain" hidden onChange={loadFile} />
      </Box>
      <TextField
        multiline
        minRows={6}
        maxRows={12}
        fullWidth
        value={csv}
        onChange={e => updateCsv(e.target.value)}
        placeholder={'email,team\nrunner1@example.com,Team Red\nrunner2@example.com,Team Blue'}
        slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } } }}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {preview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ fontSize: 14, fontWeight: 600 }}>
            {t('importMembers.summary', { rows: preview.rowCount, count: totalAdding, skipped: preview.skipped.length })}
          </Box>
          <Box sx={{ border: '1px solid var(--border)', borderRadius: '8px' }}>
            {preview.teams.map(team => (
              <Box key={team.teamId} sx={{ display: 'flex', justifyContent: 'space-between', p: '8px 12px', fontSize: 13, '&:not(:last-of-type)': { borderBottom: '1px solid var(--border)' } }}>
                <span>{team.teamName}</span>
                <Box component="span" sx={{ color: team.adding > 0 ? 'var(--fg)' : 'var(--muted)' }}>
                  {team.currentMembers} → {team.currentMembers + team.adding}
                  {team.adding > 0 && <strong> (+{team.adding})</strong>}
                </Box>
              </Box>
            ))}
          </Box>
          {preview.skipped.length > 0 && (
            <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ fontWeight: 600, mb: 0.5 }}>{t('importMembers.skippedTitle', { count: preview.skipped.length })}</Box>
              <Box sx={{ maxHeight: 180, overflowY: 'auto', fontSize: 13 }}>
                {preview.skipped.map(row => (
                  <Box key={row.line} sx={{ overflowWrap: 'anywhere' }}>
                    {t('importMembers.skippedRow', { line: row.line, email: row.email || '—' })}{' '}
                    {t(`importMembers.reasons.${row.reason}`, { team: row.teamName, currentTeam: row.currentTeam })}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}
        </Box>
      )}
    </ContestFormDialogLayout>
  );
};
